/**
 * Responsive layout for the primary canvas toolbar and its flyouts.
 * Prefers a horizontal bar centered above the bottom edge of the canvas
 * content area (excluding the floating chat / session shell on the right).
 * Falls back to the legacy vertical strip on the left when space is tight.
 */

import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { CanvasToolbarOrientation } from "../components/toolbar";
import { CANVAS_TOOLBAR_SUBMENU_GAP_PX } from "../components/toolbar/canvasToolbarLayout.constants";
import { useCanvasWrapperLayout } from "../hooks/useCanvasWrapperLayout";

export type CanvasToolbarDock = "bottom" | "left";

export {
  CANVAS_PRIMARY_TOOLBAR_VERTICAL_WIDTH_PX,
  CANVAS_TOOLBAR_LEFT_DOCK_INSET_PX,
  CANVAS_TOOLBAR_SUBMENU_GAP_PX,
} from "../components/toolbar/canvasToolbarLayout.constants";

/** Minimum width needed to dock the primary toolbar horizontally at the bottom. */
export const CANVAS_PRIMARY_TOOLBAR_HORIZONTAL_MIN_WIDTH = 360;

/** Horizontal inset from the canvas content edges when docked at the bottom. */
const BOTTOM_DOCK_HORIZONTAL_INSET_PX = 16;

export interface CanvasToolbarLayoutValue {
  orientation: CanvasToolbarOrientation;
  dock: CanvasToolbarDock;
  /** Shared gap between toolbar shell and flyout submenus (px). */
  submenuGapPx: number;
  tooltipSide: "top" | "right" | "bottom" | "left";
  /** Radix dropdown / popover side for toolbar submenus. */
  menuSide: "top" | "right" | "bottom" | "left";
  flyoutOrientation: CanvasToolbarOrientation;
  /** Wrapper positioning for the primary toolbar chrome. */
  containerClassName: string;
  popoverSide: "top" | "right" | "bottom" | "left";
  popoverSideOffset: number;
  swatchOrientation: "horizontal" | "vertical";
}

function layoutForDock(dock: CanvasToolbarDock): CanvasToolbarLayoutValue {
  const gap = CANVAS_TOOLBAR_SUBMENU_GAP_PX;

  if (dock === "left") {
    return {
      orientation: "vertical",
      dock,
      submenuGapPx: gap,
      tooltipSide: "right",
      menuSide: "right",
      flyoutOrientation: "vertical",
      containerClassName:
        "absolute left-4 top-1/2 z-10 flex -translate-y-1/2 flex-row items-center",
      popoverSide: "right",
      popoverSideOffset: gap,
      swatchOrientation: "vertical",
    };
  }

  return {
    orientation: "horizontal",
    dock,
    submenuGapPx: gap,
    tooltipSide: "top",
    menuSide: "top",
    flyoutOrientation: "horizontal",
    containerClassName:
      "absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center",
    popoverSide: "top",
    popoverSideOffset: gap,
    swatchOrientation: "horizontal",
  };
}

const CanvasToolbarLayoutContext =
  createContext<CanvasToolbarLayoutValue | null>(null);

export interface CanvasToolbarLayoutProviderProps {
  children: ReactNode;
  /** Force a dock/orientation (e.g. tests). Omit to resolve from available width. */
  orientation?: CanvasToolbarOrientation;
}

export function CanvasToolbarLayoutProvider({
  children,
  orientation: orientationOverride,
}: CanvasToolbarLayoutProviderProps) {
  const { controlsMaxWidth } = useCanvasWrapperLayout();

  const value = useMemo((): CanvasToolbarLayoutValue => {
    if (orientationOverride === "vertical") {
      return layoutForDock("left");
    }
    if (orientationOverride === "horizontal") {
      return layoutForDock("bottom");
    }

    const availableWidth =
      controlsMaxWidth > 0
        ? controlsMaxWidth - BOTTOM_DOCK_HORIZONTAL_INSET_PX * 2
        : 0;

    const dock: CanvasToolbarDock =
      availableWidth >= CANVAS_PRIMARY_TOOLBAR_HORIZONTAL_MIN_WIDTH
        ? "bottom"
        : "left";

    return layoutForDock(dock);
  }, [controlsMaxWidth, orientationOverride]);

  return (
    <CanvasToolbarLayoutContext.Provider value={value}>
      {children}
    </CanvasToolbarLayoutContext.Provider>
  );
}

export function useCanvasToolbarLayout(): CanvasToolbarLayoutValue {
  const ctx = useContext(CanvasToolbarLayoutContext);
  if (!ctx) {
    throw new Error(
      "useCanvasToolbarLayout must be used within CanvasToolbarLayoutProvider",
    );
  }
  return ctx;
}

/** Optional consumer — returns null outside the provider (e.g. floating toolbars). */
export function useCanvasToolbarLayoutOptional(): CanvasToolbarLayoutValue | null {
  return useContext(CanvasToolbarLayoutContext);
}
